// Discreet cookie consent banner — appears once, dismissable, multilingual.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const STORAGE_KEY = "prieure_cookie_ack";

const TEXTS = {
  fr: {
    body: "Ce site n'utilise que des cookies strictement nécessaires (session, langue). Aucun cookie publicitaire ni de mesure d'audience tierce.",
    learnMore: "En savoir plus",
    ack: "J'ai compris",
  },
  en: {
    body: "This site only uses strictly necessary cookies (session, language). No advertising or third-party analytics cookies.",
    learnMore: "Learn more",
    ack: "Got it",
  },
  it: {
    body: "Questo sito utilizza solo cookie strettamente necessari (sessione, lingua). Nessun cookie pubblicitario o di analisi di terze parti.",
    learnMore: "Scopri di più",
    ack: "Ho capito",
  },
  es: {
    body: "Este sitio solo utiliza cookies estrictamente necesarias (sesión, idioma). Sin cookies publicitarios ni de análisis de terceros.",
    learnMore: "Saber más",
    ack: "Entendido",
  },
  de: {
    body: "Diese Website verwendet ausschließlich technisch notwendige Cookies (Sitzung, Sprache). Keine Werbe- oder Drittanbieter-Analyse-Cookies.",
    learnMore: "Mehr erfahren",
    ack: "Verstanden",
  },
  pt: {
    body: "Este site utiliza apenas cookies estritamente necessários (sessão, idioma). Sem cookies publicitários ou de análise de terceiros.",
    learnMore: "Saber mais",
    ack: "Compreendi",
  },
};

export default function CookieBanner() {
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ack = window.localStorage.getItem(STORAGE_KEY);
    if (!ack) {
      const timer = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  const t = TEXTS[locale] || TEXTS.fr;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookies"
      style={{
        position: "fixed",
        bottom: "1rem",
        left: "1rem",
        right: "1rem",
        maxWidth: "min(640px, calc(100vw - 2rem))",
        marginLeft: "auto",
        marginRight: "auto",
        zIndex: 80,
        background: "rgba(245,240,228,0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid var(--border)",
        borderLeftWidth: "3px",
        borderLeftColor: "var(--gold)",
        boxShadow: "0 10px 40px rgba(20,16,12,0.25)",
        padding: "1rem 1.25rem",
        color: "var(--ink-2)",
        fontSize: "0.85rem",
        lineHeight: 1.55,
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <span className="font-caps text-[0.6rem] block mb-1"
                style={{ letterSpacing: "0.22em", color: "var(--gold)" }}>
            ◇ COOKIES ◇
          </span>
          {t.body}{" "}
          <Link to="/confidentialite" className="link-underline" style={{ color: "var(--cinnabar)" }}>
            {t.learnMore} →
          </Link>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="cookie-ack"
          className="btn btn-primary"
          style={{ fontSize: "0.7rem", padding: "0.55rem 1.1rem", letterSpacing: "0.1em", flexShrink: 0 }}
        >
          {t.ack}
        </button>
      </div>
    </div>
  );
}
